import { createFileRoute, redirect, Link, useNavigate } from '@tanstack/react-router';
import { useEffect, useMemo } from 'react';
import { useArticle, useRecordArticleView } from '@/queries/articles';
import { useCollectionTree } from '@/queries/article-collections';
import { useUserTier } from '@/queries/subscriptions';
import { flattenArticles } from '@/types/article-collection';
import { ArticleViewer } from '@/components/ArticleViewer';
import { ArticleMeta } from '@/components/ArticleMeta';
import { CollectionReadingSidebar } from '@/components/CollectionReadingSidebar';
import { CollectionReadingNav } from '@/components/CollectionReadingNav';
import { CircularLoader } from '@/components/CircularLoader';
import ArrowLeftIcon from '@/assets/common/arrow-left.svg?react';
import styles from './collectionReading.module.scss';

export const Route = createFileRoute(
  '/articles/collections/$collectionId/articles/$articleId'
)({
  component: CollectionReadingPage,
  beforeLoad: ({ context }) => {
    const { user, authLoading } = context as { user: any; authLoading: boolean };
    if (authLoading) return;
    if (!user) {
      throw redirect({ to: '/' });
    }
  },
});

function CollectionReadingPage() {
  const { collectionId, articleId } = Route.useParams();
  const navigate = useNavigate();
  const userTier = useUserTier();

  // Redirect foundation tier users to regular article view
  useEffect(() => {
    if (userTier === 'foundation') {
      navigate({
        to: '/articles/$articleId',
        params: { articleId },
        replace: true,
      });
    }
  }, [userTier, articleId, navigate]);

  // Fetch the tree for the collection
  const {
    data: collectionData,
    isLoading: isTreeLoading,
    error: treeError,
  } = useCollectionTree(collectionId);

  // Fetch the current article
  const {
    data: article,
    isLoading: isArticleLoading,
    error: articleError,
  } = useArticle(articleId);

  const recordView = useRecordArticleView();

  // Record view when article loads
  useEffect(() => {
    if (article && articleId) {
      recordView.mutate(articleId);
    }
  }, [articleId, article?.id]);

  // Flatten articles for navigation
  const flattenedArticles = useMemo(() => {
    if (!collectionData) return [];
    return flattenArticles(
      collectionData.tree,
      collectionData.id,
      collectionData.name
    );
  }, [collectionData]);

  const isLoading = isTreeLoading || isArticleLoading;
  const error = treeError || articleError;

  // Show loading while redirecting foundation users
  if (userTier === 'foundation') {
    return (
      <div className={styles.readingPage}>
        <div className={styles.readingPage__loading}>
          <CircularLoader />
        </div>
      </div>
    );
  }

  if (isLoading) {
    return (
      <div className={styles.readingPage}>
        <div className={styles.readingPage__loading}>
          <CircularLoader />
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className={styles.readingPage}>
        <div className={styles.readingPage__error}>
          <p>Error loading content: {error.message}</p>
          <Link to="/articles/collections/$collectionId" params={{ collectionId }}>
            Return to Collection
          </Link>
        </div>
      </div>
    );
  }

  if (!collectionData || !article) {
    return (
      <div className={styles.readingPage}>
        <div className={styles.readingPage__notFound}>
          <p>Content not found</p>
          <Link to="/articles/collections/$collectionId" params={{ collectionId }}>
            Return to Collection
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className={styles.readingPage}>
      {/* Header with collection name */}
      <header className={styles.header}>
        <div className={styles.header__left}>
          <Link
            to="/articles/collections/$collectionId"
            params={{ collectionId }}
            className={styles.header__backButton}
          >
            <ArrowLeftIcon />
            Back
          </Link>

          <nav className={styles.header__breadcrumbs}>
            <span className={styles.header__breadcrumbCurrent}>
              {collectionData.name}
            </span>
          </nav>
        </div>
      </header>

      {/* Main content area */}
      <main className={styles.main}>
        <div className={styles.article}>
          <article className={styles.article__content}>
            <h1 className={styles.article__title}>{article.title}</h1>
            <ArticleMeta article={article} />
            <ArticleViewer content={article.content} />
          </article>

          {/* Prev/Next navigation */}
          <CollectionReadingNav
            flattenedArticles={flattenedArticles}
            currentArticleId={articleId}
            rootCollectionId={collectionId}
          />
        </div>
      </main>

      {/* Right sidebar */}
      <aside className={styles.sidebar}>
        <CollectionReadingSidebar
          tree={collectionData.tree}
          flattenedArticles={flattenedArticles}
          currentArticleId={articleId}
          rootCollectionId={collectionId}
        />
      </aside>
    </div>
  );
}
